import { BigDataEventConstants } from "./constants/big-data-constants";
import { DdboxConstants } from "./constants/ddbox-constants";
import { EventType } from "./constants/event-type-constants";
import { DOMHelper } from "./helpers/dom-helper";
import { BigData } from "./marketing-client-bigdata";
import { Capping, CappingData } from "./marketing-client-capping";
import { MarketingClientCustomDataLayer } from "./marketing-client-custom-datalayer";
import { CollectionIdsObject, CtaObject, Utils } from "./utils/utils";
import { TimerCountdown } from "./helpers/timer-countdown";

export class Ddbox {
    ddboxIsVisible = false;
    isMobile: Boolean;
    mouseleave = false;
    capping: Capping;
    collectionIdsObject: CollectionIdsObject;
    ctaObject: CtaObject;
    timerCountdown: TimerCountdown;

    constructor(isMobile: Boolean, capping: Capping) {
        this.isMobile = isMobile;
        this.capping = capping;
    }

    init() {
        const ddbox = DOMHelper.querySelector(DdboxConstants.class) as HTMLElement;

        if (ddbox && ddbox.parentElement) {
            this.collectionIdsObject = Utils.prepareCollectionIdsObject(ddbox.parentElement);
            this.ctaObject = Utils.prepareCtaObject(ddbox.parentElement);

            this.sendCapping();
            this.prepareDdbox(ddbox);
            this.initTimer(ddbox?.dataset?.timerDdbox as string);
        }
    }

    initTimer(dateEnd: string) {
        if (!dateEnd) return;

        this.timerCountdown = new TimerCountdown(dateEnd, DdboxConstants.timerSelector);
    }

    sendCapping(onDisplay = false) {
        const attribute = BigData.prepareDataOceanAttribute(this.ctaObject, this.collectionIdsObject),
            eventName = onDisplay ? BigDataEventConstants.onDisplay : BigDataEventConstants.onLoad;

        this.capping.sendCapping(this.collectionIdsObject as CappingData, onDisplay);
        BigData.sendDataOceanEvent(eventName, attribute);
    }

    prepareDdbox(box: HTMLElement) {
        const isOnExitPopUp: string | undefined = box.dataset.onExitPopUp,
            isUserInactivityTimeout: string | undefined = box.dataset.userInactivityTimeout,
            isRw: string | undefined = box.dataset.ddboxWithRw;

        box.style.display = "none";
        this.bindActions(box);
        this.ddboxIsVisible = true;

        if (this.isMobile) {
            if (EventType.ORIENTATION_CHANGE in window) {
                window.addEventListener(EventType.ORIENTATION_CHANGE, () => { this.shrinkIfTooBig(box) });
            } else {
                window.addEventListener(EventType.RESIZE, () => { this.shrinkIfTooBig(box) });
            }
        }

        if (isRw) {
            return;
        }

        if (isOnExitPopUp && !this.isMobile) {
            this.onExitPopUp(box);
        } else if (isUserInactivityTimeout) {
            this.onUserInactivityTimeout(box);
        } else {
            this.onDelay(box);
        }
    }

    onExitPopUp(box: HTMLElement) {
        document.addEventListener(EventType.MOUSELEAVE, (event) => {
            if (event.clientY <= 0 && !this.mouseleave) {
                this.showDdbox(box)
                this.mouseleave = true;
            }
        })
    }

    onUserInactivityTimeout(box: HTMLElement) {
        const attribute: string | undefined = box.dataset.userInactivityTimeoutValue,
            timeout: number | undefined = Utils.attributeToNumber(attribute),
            showDdboxAndSendCapping = () => {
                this.showDdbox(box);
            };

        Utils.showElementAfterTimeout(showDdboxAndSendCapping, timeout);
    }

    onDelay(box: HTMLElement) {
        setTimeout(() => {
            this.showDdbox(box);
        }, DdboxConstants.delay);
    }

    showDdbox(box: HTMLElement) {
        const target: HTMLElement = document.body,
            isDynamicUtilization: string =
                box.parentElement?.dataset.dynamicUtilization || "",
            links: NodeListOf<Element> = box.querySelectorAll(`a:not([href="#"])`),
            { campaignId = "", actionId = "", touchPointId = "" } = this.collectionIdsObject || {},
            { cta = "", ctaCategory = "" } = this.ctaObject || {};

        box.dataset.campaignId = campaignId;
        box.dataset.actionId = actionId;
        box.dataset.touchPointId = touchPointId;
        box.dataset.dm = "true";
        target.append(box);

        if (isDynamicUtilization && links.length > 0) {
            links.forEach(link => {
                this.setHref(link, campaignId, actionId, touchPointId);
            })
        }

        if (this.isMobile) {
            this.shrinkIfTooBig(box);
            this.lockBody();
        }

        this.timerCountdown?.init();

        box.style.display = "block";
        box.classList.add(DdboxConstants.visible);

        MarketingClientCustomDataLayer.sendDMEvent(ctaCategory, cta);
        this.sendCapping(true);
    }

    setHref(link: Element, campaignId: string, actionId: string, touchPointId: string) {
        const href: string | null = link.getAttribute("href"),
            hasParameters: Boolean = href && href.indexOf("?") > -1 ? true : false,
            dmParams = `${href}${hasParameters ? "&" : "?"
                }campaignId=${campaignId}&actionId=${actionId}&touchPointId=${touchPointId}`;

        link.setAttribute("href", dmParams);
    }

    bindActions(box: HTMLElement) {
        const closeButtons: Array<Element> = DOMHelper.querySelectorAsArray(DdboxConstants.closeButton, box);

        closeButtons.forEach(closeButton => {
            closeButton.addEventListener(EventType.CLICK, (event) => {
                event.preventDefault ? event.preventDefault() : event.returnValue = false;
                this.closeDdbox(box)
            })
        })
    }

    closeDdbox(box: HTMLElement) {
        box.classList.remove(DdboxConstants.visible);

        if (this.isMobile) {
            this.unLockBody();
        }

        setTimeout(function () {
            box.remove();
        }, 300);
    }

    lockBody() {
        document.body.classList.add(DdboxConstants.locked);
    }

    unLockBody() {
        document.body.classList.remove(DdboxConstants.locked);
    }

    shrinkIfTooBig(box: HTMLElement) {
        const image: HTMLImageElement | null = box.querySelector("img");

        if (image) {
            if (image.complete) {
                this.resizeImage(image, box);
            } else {
                const tmpImage = new Image();

                tmpImage.onload = () => {
                    this.resizeImage(tmpImage, box);
                };

                tmpImage.src = image.src;
            }
        }
    }

    resizeImage(image: HTMLImageElement, box: HTMLElement) {
        const imageHeight = image.height,
            windowHeight = window.innerHeight;

        if (imageHeight > windowHeight) {
            box.classList.add(DdboxConstants.tooBig);
            image.style.height = windowHeight + "px";
        } else {
            box.classList.remove(DdboxConstants.tooBig);
            image.style.height = "auto";
        }

        // this.fitLinkMap(box, image);
        //probably need timeout for old android devices
    }
}
